import React from 'react';
import { motion } from 'motion/react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Policy</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Last Updated: January 29, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              Welcome to Goodikids. We are committed to protecting the privacy and safety of children and families who use our learning application. This Privacy Policy explains what information we collect, how we use it, how we protect it, and the rights parents have related to their child’s data.
            </p>
            <p className="text-gray-600 mb-6">
              By using Goodikids, you agree to the practices described in this Privacy Policy.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">2. Information We Collect</h2>
            <p className="text-gray-600 mb-6">
              We collect minimal, child-safe information necessary to provide our learning features.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">A. Information Provided by Parents/Guardians</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Parent/Guardian name and email address</li>
              <li>Mobile number (if OTP login is used)</li>
              <li>Child’s name or nickname (optional)</li>
              <li>Child’s age group (5–6, 7–8, etc.)</li>
              <li>Selected avatar and learning preferences</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">B. Information About Child’s Learning Activity</h3>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>MCQ results and performance</li>
              <li>Learning progress (themes and subtopics completed)</li>
              <li>Badges, rewards, and certificates earned</li>
              <li>Time spent on lessons</li>
              <li>Streaks and activity history</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">C. Automatically Collected Information</h3>
            <p className="text-gray-600 mb-4">
              Used only for app performance, security, and improvement:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Device type and model</li>
              <li>Operating system version</li>
              <li>App version and crash reports</li>
              <li>Non-identifiable analytics (e.g., screens visited, time spent)</li>
              <li>Approximate city-level location (for analytics only)</li>
            </ul>

            <h4 className="text-xl font-bold text-gray-900 mb-2 mt-4">✔ Country or Region Information</h4>
            <p className="text-gray-600 mb-4">
              We may determine the user’s country or region based on IP address or parent selection. This is used only to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Show correct subscription pricing</li>
              <li>Display the right currency</li>
              <li>Apply regional language settings</li>
              <li>Comply with regional privacy requirements</li>
            </ul>
            <p className="text-gray-600 mb-6">
              This does NOT include exact GPS location or street-level tracking.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">We Do NOT Collect</h3>
            <ul className="list-none pl-0 text-gray-600 mb-6 space-y-2">
              <li className="flex items-center gap-2">❌ Exact GPS location</li>
              <li className="flex items-center gap-2">❌ Photos, videos, or voice recordings</li>
              <li className="flex items-center gap-2">❌ School or home address</li>
              <li className="flex items-center gap-2">❌ Contacts or call logs</li>
              <li className="flex items-center gap-2">❌ Advertising ID for targeted ads</li>
              <li className="flex items-center gap-2">❌ Biometric or sensitive personal data</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">
              We use collected information to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Provide and operate the Goodikids learning experience</li>
              <li>Personalize lessons based on age and progress</li>
              <li>Track learning performance and show reports</li>
              <li>Issue badges, certificates, and rewards</li>
              <li>Improve app performance, stability, and features</li>
              <li>Send verification OTPs and important updates</li>
              <li>Display correct pricing based on region</li>
            </ul>
            <p className="text-gray-600 mb-4 mt-6">
              We do NOT:
            </p>
            <ul className="list-none pl-0 text-gray-600 mb-6 space-y-2">
              <li className="flex items-center gap-2">❌ Sell data</li>
              <li className="flex items-center gap-2">❌ Use data for advertising</li>
              <li className="flex items-center gap-2">❌ Track users across apps or websites</li>
              <li className="flex items-center gap-2">❌ Share child data with advertisers</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">4. Children’s Privacy (COPPA Compliance)</h2>
            <p className="text-gray-600 mb-6">
              Goodikids follows the Children’s Online Privacy Protection Act (COPPA).
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">A. Parental Consent</h3>
            <p className="text-gray-600 mb-4">
              We do not collect personal information from children under 13 without verifiable parental consent, verified through:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>OTP sent to parent’s email or phone</li>
              <li>Parent account login</li>
              <li>Parent confirmation inside the app</li>
            </ul>
            <p className="text-gray-600 mb-6">
              If parental consent is not provided, we only collect non-identifiable technical data required for internal operations.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">B. Parental Rights</h3>
            <p className="text-gray-600 mb-4">
              Parents/Guardians can at any time:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Review their child’s personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request full deletion of child’s data</li>
              <li>Withdraw consent</li>
              <li>Disable or delete child’s account</li>
            </ul>
            <p className="text-gray-600 mb-6">
              To exercise rights: <a href="mailto:privacy@goodikids.com" className="text-orange-500 hover:text-orange-600">privacy@goodikids.com</a>
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">5. Third-Party Service Providers</h2>
            <p className="text-gray-600 mb-4">
              We use trusted services solely for app operations:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Cloud hosting/databases (Firebase, AWS, or similar)</li>
              <li>Crash reporting (for stability)</li>
              <li>Analytics (for app improvement only)</li>
              <li>Email/SMS delivery services</li>
              <li>Payment processing (if applicable)</li>
            </ul>
            <p className="text-gray-600 mb-4">
              These providers:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Receive only the minimum required information</li>
              <li>Are bound by confidentiality agreements</li>
              <li>Cannot use data for advertising or other purposes</li>
            </ul>
            <p className="text-gray-600 mb-6">
              We do NOT use advertising networks.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">6. Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain data only as long as necessary:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Parent/guardian account data → kept while account is active</li>
              <li>Child learning data → deleted after 24 months of inactivity or upon parent request</li>
              <li>Crash logs & analytics → retained up to 12 months</li>
            </ul>
            <p className="text-gray-600 mb-6">
              After these periods, data is securely deleted or anonymized.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">7. Data Security</h2>
            <p className="text-gray-600 mb-4">
              We protect information using:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Encryption (in transit and at rest)</li>
              <li>Secure cloud infrastructure</li>
              <li>Strict access controls</li>
              <li>Regular security monitoring</li>
            </ul>
            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">Data Breach Notification</h3>
            <p className="text-gray-600 mb-4">
              In the event of a data breach affecting user information, we will notify:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Affected parents</li>
              <li>Relevant authorities (if required) within the legally required time frame.</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">8. Your Rights (Parents/Guardians)</h2>
            <p className="text-gray-600 mb-4">
              Parents have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Access the personal data we hold</li>
              <li>Request correction</li>
              <li>Request deletion</li>
              <li>Withdraw consent</li>
              <li>Opt out of parent-targeted communication</li>
            </ul>
            <p className="text-gray-600 mb-6">
              To submit a request: <a href="mailto:privacy@goodikids.com" className="text-orange-500 hover:text-orange-600">privacy@goodikids.com</a>
              <br />
              We respond within 30 days.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">9. Cookies & Tracking Technologies</h2>
            <p className="text-gray-600 mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Improve performance</li>
              <li>Maintain login sessions</li>
              <li>Analyze non-identifiable usage patterns</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Cookies do not track children across apps or websites. Parents can disable cookies via device or browser settings.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">10. GDPR Compliance (European Users)</h2>
            <p className="text-gray-600 mb-6">
              If you are located in the EU or UK, the following applies:
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">A. Legal Basis for Processing</h3>
            <p className="text-gray-600 mb-4">
              We process data based on:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Parental consent</li>
              <li>Performance of a service (providing learning app)</li>
              <li>Legitimate interests (security, app functionality)</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">B. GDPR Rights</h3>
            <p className="text-gray-600 mb-4">
              EU users may:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Request access</li>
              <li>Request deletion</li>
              <li>Request correction</li>
              <li>Request data portability</li>
              <li>Object to certain processing</li>
              <li>Withdraw consent at any time</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">C. Child Age Requirements</h3>
            <p className="text-gray-600 mb-6">
              For EU users, parental consent is required for children under 16, unless local law sets a lower age (13–16).
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">D. International Data Transfers</h3>
            <p className="text-gray-600 mb-6">
              Data may be stored on secure servers outside the EU. We use Standard Contractual Clauses (SCCs), or equivalent legal protections to safeguard international transfers.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">11. CCPA/CPRA Compliance (California Users)</h2>
            <p className="text-gray-600 mb-6">
              For California users:
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">A. California User Rights</h3>
            <p className="text-gray-600 mb-4">
              You may:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Request details of personal data collected</li>
              <li>Request deletion</li>
              <li>Request correction</li>
              <li>Know whether data is sold or shared</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">B. Do Not Sell My Personal Information</h3>
            <p className="text-gray-600 mb-6">
              Goodikids does NOT sell or share personal information, including child data.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">C. Non-Discrimination</h3>
            <p className="text-gray-600 mb-6">
              We do not discriminate against users who exercise their privacy rights.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">12. Changes to This Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this policy to reflect changes in features or legal requirements. Significant updates will be communicated via email or in-app alerts.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">13. Contact Us</h2>
            <p className="text-gray-600 mb-6">
              For any questions or concerns:
              <br />
              📧 Email: <a href="mailto:privacy@goodikids.com" className="text-orange-500 hover:text-orange-600">privacy@goodikids.com</a>
              <br />
              📞 Phone: +1 (555) 123-4567
              <br />
              🌐 Website: <a href="https://www.goodikids.com/" className="text-orange-500 hover:text-orange-600">https://www.goodikids.com/</a>
            </p>
            <p className="text-gray-500 text-sm mt-12">
              ©️ 2025 Goodikids. All rights reserved.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;